// Display-name masking — two-side anonymity for studio ↔ client communication.
//
//   Same-side viewer (studio sees studio, client sees client) → full name + handle
//   Cross-side viewer (artist sees client, client sees artist) → handle only
//
// Why: artists could Google "Chloe Mattel @ Animaj" → poaching risk. And we
// already strip emails. Now we also strip names across the studio/client line
// so the only identity exchanged is a project-scoped @handle.
//
// Studio members see each other's full names freely (they work together).
// Clients see other clients on their side freely (rare, future-proofing).
//
// Used everywhere a name is rendered: submission cards, annotation pins,
// chat bubbles, @mention dropdowns, audit logs.

const CLIENT_ROLES = new Set(['client_producer', 'client_director', 'client', 'client_admin']);

// Cross-side fallback when the studio author has no @handle yet — clients
// see "Studio Artist" instead of a real name. Configurable in one place so
// rebrands stay consistent.
const STUDIO_ANON_FALLBACK = 'Studio Artist';
const CLIENT_ANON_FALLBACK = 'Client Reviewer';

function isClient(role) {
  return CLIENT_ROLES.has(String(role || '').toLowerCase());
}

/**
 * Resolve the safe display string for an author/submitter, given the viewer.
 *
 * @param {object} author      — { name?, full_name?, handle?, role?, author_role?, submitter_role? }
 * @param {string} viewerRole  — the current logged-in user's role (from useAuth)
 * @returns {string}             A friendly display string. NEVER an email.
 */
export function displayNameFor(author, viewerRole) {
  if (!author) return STUDIO_ANON_FALLBACK;
  const authorRole = author.role || author.author_role || author.submitter_role || author.actor_role || '';
  const handle = (author.handle || author.author_handle || author.submitter_handle || author.actor_handle || '').trim();
  const fullName = (author.name || author.full_name || author.author_name || author.submitter_name || author.actor_name || '').trim();

  const viewerIsClient = isClient(viewerRole);
  const authorIsClient = isClient(authorRole);

  // Cross-side communication → mask the name, leave only @handle. If no
  // handle, fall back to the side-appropriate anonymous label so the client
  // never sees the studio member's real name (or vice-versa).
  if (viewerIsClient !== authorIsClient) {
    if (handle) return `@${handle}`;
    return authorIsClient ? CLIENT_ANON_FALLBACK : STUDIO_ANON_FALLBACK;
  }
  // Same-side → full name preferred, fallback to @handle, then anon label
  return fullName || (handle ? `@${handle}` : STUDIO_ANON_FALLBACK);
}

/**
 * Returns just the handle portion (without `@` prefix) — useful when the UI
 * already renders an `@` symbol manually with cyan styling.
 */
export function handleFor(author) {
  return (author?.handle || author?.author_handle || author?.submitter_handle || '').trim();
}

/**
 * Should we render the @handle pill next to the name?
 * Same-side viewer: yes (visual aid for mentions). Cross-side: no — the
 * displayNameFor() already returns the @handle so a separate pill is redundant.
 */
export function shouldRenderHandlePill(author, viewerRole) {
  if (!author) return false;
  const authorRole = author.role || author.author_role || author.submitter_role || '';
  return isClient(viewerRole) === isClient(authorRole) && !!handleFor(author);
}
